// The chip that follows the cursor while a disease, phenotype or genotype is
// dragged from the legend onto the chart.
//
// WHY IT WAS INVISIBLE. Each legend appended an unpositioned block <div> to
// <body>. App.css makes `html, body { overflow: hidden; height: 100% }` and the
// app root is 100vh, so the div landed at top === innerHeight: below the viewport
// and inside body's overflow clip. WebKit builds the drag bitmap by painting the
// frame restricted to that node, so the clip applies and Safari dragged a fully
// transparent 1280x34 image — the drop still worked, nothing showed under the
// cursor. (Measured in Playwright WebKit and Chromium; the timing of the removal
// was never the cause — the phenotype legend kept its chip until dragend and was
// just as invisible.)
//
// THE RULES, each for a defect:
// - `position: fixed` at 0,0 — inside the viewport and out of body's clip. Never
//   `display: none` / `visibility: hidden` / `opacity: 0`, and never fully
//   offscreen (`top: -1000px`): WebKit then snapshots nothing, and Safari 17.1
//   cancels the drag outright.
// - The element handed to setDragImage is itself shrink-wrapped. The old outer
//   <div> was display:block, so the "chip" was a viewport-wide strip.
// - textContent, never innerHTML. The label is a stored disease / phenotype /
//   gene name, and an `<img onerror>` in one ran the moment anyone dragged it.
// - Removed on the next animation frame. Every engine has snapshotted by then
//   (WebKit and Chromium synchronously in dragstart, Gecko as dragstart's default
//   action), and it is before the next paint, so the chip never shows at 0,0.
//   Removing it synchronously inside dragstart breaks all three.
// - A border, not a box-shadow: a shadow paints outside the box and is cropped
//   out of the bitmap.

const CHIP_STYLE = {
  position: 'fixed',
  top: '0px',
  left: '0px',
  zIndex: '2147483647',
  pointerEvents: 'none',
  display: 'inline-block',
  width: 'max-content',
  maxWidth: '320px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
  padding: '6px 14px',
  borderRadius: '16px',
  fontSize: '13px',
  fontWeight: '600',
  lineHeight: '18px'
};

// No timer fallback: the caller's dragend removes the chip too, so an
// environment without requestAnimationFrame still cleans up.
const nextFrame = (cb) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(cb);
  }
};

/**
 * Build the chip, hand it to the browser as the drag image, and schedule its
 * removal. Call from a `dragstart` handler.
 *
 * @param {DragEvent} event
 * @param {{ label: string, background: string, color: string, borderColor?: string }} look
 * @returns {() => void} idempotent cleanup — call it from `dragend` as a backstop
 */
export const showLegendDragChip = (
  event,
  { label, background, color, borderColor }
) => {
  const dataTransfer = event?.dataTransfer;
  if (
    typeof document === 'undefined' ||
    typeof dataTransfer?.setDragImage !== 'function'
  ) {
    return () => {};
  }

  const chip = document.createElement('div');
  chip.className = 'legend-drag-chip';
  chip.setAttribute('aria-hidden', 'true');
  chip.textContent = label == null ? '' : String(label);
  Object.assign(chip.style, CHIP_STYLE, {
    background,
    color,
    // Opaque: Safari before 26 bakes whatever is on screen at the chip's rect
    // into its transparent pixels (WebKit 266025), so a translucent border
    // picked up the app header behind it.
    border: `1px solid ${borderColor || '#bdbdbd'}`
  });

  document.body.appendChild(chip);

  // Hotspot inside the image, near its left edge: the chip reads as being carried
  // by the cursor rather than hovering somewhere to one side of it.
  const rect = chip.getBoundingClientRect();
  dataTransfer.setDragImage(
    chip,
    Math.min(16, rect.width / 2),
    rect.height / 2
  );

  let removed = false;
  const remove = () => {
    if (removed) return;
    removed = true;
    chip.remove();
  };
  nextFrame(remove);
  return remove;
};
